import get from 'lodash/get';
import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanLoad,
  Params,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree,
} from '@angular/router';

import { StatsigService } from '@services/statsig.service';

interface IFeatureFlagObject {
  statsigFlagName?: string;
  value?: string | number | boolean;
  featureFlagType?: FEATURE_FLAG_TYPES;
  valuePath?: string;
}

export enum FEATURE_FLAG_TYPES {
  BOOLEAN = 'boolean',
  JSON = 'json',
}

export interface IStatsigFeatureGuardConfig {
  statsigFlagName?: string;
  featureFlags?: [IFeatureFlagObject];
  value?: string | number | boolean;
  useParams?: boolean;
  pathToReplace?: string;
  replacePathWith?: string;
  replaceFirstMatchOnly?: boolean;
  redirectTo?: string;
  valuePath?: string;
  featureFlagType?: FEATURE_FLAG_TYPES;
  excludeUrls?: string[];
  anyFlagEnabledStrategy?: boolean;
}

@Injectable()
export class StatsigFeatureGuard implements CanLoad, CanActivate {
  constructor(private router: Router, private statsigService: StatsigService) {}

  /**
   * redirectTo - used as redirectPath
   * replacePathWith - used to replace current path name with given path in the existing URL
   * useParams - to use matrixparams
   */
  public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    const urlTree: UrlTree = this.router.parseUrl(state.url);
    const urlSegments: UrlSegment[] = urlTree.root.children.primary.segments || [];
    return this.evaludateGuard(urlSegments, route);
  }

  public canLoad(route: Route, segments: UrlSegment[]): boolean {
    return this.evaludateGuard(segments, route);
  }

  private evaludateGuard(urlSegments: UrlSegment[], route: Route | ActivatedRouteSnapshot): boolean {
    const config: IStatsigFeatureGuardConfig = route.data.statsigFeatureGuard;
    const routing: Route = route instanceof ActivatedRouteSnapshot ? route.routeConfig : route;
    let redirectUrlArray: [Params | string] = [config.redirectTo || '/'];

    const pathToReplace = config.pathToReplace || routing.path;

    const excludedUrls = config.excludeUrls;
    const isRouteExcluded = (segments: UrlSegment[], excludedPaths: string[]): boolean => {
      return segments.map(segment => segment.path)
        .some(path => excludedPaths.includes(path));
    };
    if (excludedUrls && isRouteExcluded(urlSegments, excludedUrls)) return true;

    if (config.replacePathWith && urlSegments.length > 0) {
      const findAndReplaceFirstMatchOnly: (segments: UrlSegment[], replaceWith: string) => Params | string =
        (segments: UrlSegment[], replaceWith: string): Params | string => {
          const index = segments.findIndex((segment: UrlSegment) => segment.path === routing.path);
          if (index >= 0) segments[index].path = replaceWith;
          return segments.map(segment => segment.path).join('/');
        }
      ;

      redirectUrlArray = config.replaceFirstMatchOnly ?
        [findAndReplaceFirstMatchOnly(urlSegments, config.replacePathWith)] :
        [urlSegments
          // replace the existing url with redirectPath
        .map((urlSegment: UrlSegment) => {
          return urlSegment.path === pathToReplace ? config.replacePathWith : urlSegment.path;
        })
        .join('/')];
    }
    if (config.useParams && urlSegments.length > 0) {
      const params: Params = urlSegments[urlSegments.length - 1].parameters || {};
      redirectUrlArray.push(params);
    }
    if (config.featureFlags?.length) {
      const isAllowed = config.anyFlagEnabledStrategy
        ? this.isAnyFeatureFlagEnabled(config.featureFlags)
        : this.checkForMultipleFeatureFlags(config.featureFlags);

      if (!isAllowed) this.router.navigate(redirectUrlArray);
      return isAllowed;
    }
    if (this.evaluateFeatureFlag(config)) return true;
    else {
      this.router.navigate(redirectUrlArray);
      return false;
    }
  }

  private checkForMultipleFeatureFlags(featureFlags: [IFeatureFlagObject]): boolean {
    return featureFlags.every(this.evaluateFeatureFlag.bind(this));
  }

  private isAnyFeatureFlagEnabled(featureFlags: [IFeatureFlagObject]): boolean {
    return featureFlags.some(this.evaluateFeatureFlag.bind(this));
  }

  private evaluateFeatureFlag(config: IFeatureFlagObject): boolean {
    const featureFlagExpectedValue = config.hasOwnProperty('value') ? config.value : true;
    const expectedTypeOf = config['typeOf'];
    const featureFlagType = config.hasOwnProperty('featureFlagType') ? config.featureFlagType : FEATURE_FLAG_TYPES.BOOLEAN;
    const valuePath = config.valuePath;
    const statsigFlagName = config.statsigFlagName;
    const featureFlagValue =
      featureFlagType === FEATURE_FLAG_TYPES.BOOLEAN
        ? this.statsigService.evaluateGate(statsigFlagName)
        : this.statsigService.evaluateDynamicConfig(statsigFlagName)?.value;

    if (featureFlagType === FEATURE_FLAG_TYPES.BOOLEAN && featureFlagValue === featureFlagExpectedValue) return true;
    else if (
      featureFlagType === FEATURE_FLAG_TYPES.JSON &&
      valuePath &&
      (
        get(featureFlagValue, valuePath) === featureFlagExpectedValue ||
        expectedTypeOf && typeof get(featureFlagValue, valuePath) === expectedTypeOf
      )
    ) return true;

    return false;
  }
}
